import Coordinates from "coordinate-parser"
import {
  AlertCircle,
  Eye,
  EyeOff,
  MapPin,
  Search,
  Settings,
} from "lucide-react"
import React, { useCallback, useEffect, useRef, useState } from "react"

import Toggle from "@/components/Toggle"
import {
  COVERAGE_RADIUS_MAX,
  COVERAGE_RADIUS_MIN,
  COVERAGE_RADIUS_STEP,
  type CoverageMode,
  KM_TO_MILES,
  NEAREST_STATIONS_MAX,
  NEAREST_STATIONS_MIN,
  NEAREST_STATIONS_STEP,
} from "@/components/coverage-map/constants"

interface Suggestion {
  display_name: string
  lat: string
  lon: string
}

interface StadiaSuggestion {
  properties: {
    label: string
  }
  geometry: {
    coordinates: [number, number]
  }
}

export interface SearchControlsProps {
  address: string
  setAddress: (val: string) => void
  pinnedAddress: string | null
  setPinnedAddress: (val: string | null) => void
  onSelectCoords: (lat: number, lon: number) => void
  isMetric: boolean
  setIsMetric: (val: boolean) => void
  showCoverage: boolean
  setShowCoverage: (val: boolean) => void
  onReset: () => void
  searchLocation: { lat: number; lon: number } | null
  coverageRadius: number
  setCoverageRadius: (val: number) => void
  nearestStationsCount: number
  setNearestStationsCount: (val: number) => void
  mapType: "light" | "dark"
  setMapType: (val: "light" | "dark") => void
  coverageMode: CoverageMode
  setCoverageMode: (val: CoverageMode) => void
  // Whether the SQI coverage layer is offered. Driven by the backend
  // FEATURE_SQI flag (fetched at runtime), not a build-time constant.
  sqiEnabled: boolean
  // Whether the current user is allowed to access SQI debug detail.
  sqiDebugAllowed: boolean
  debugMode: boolean
  setDebugMode: (val: boolean) => void
}

const SearchControls: React.FC<SearchControlsProps> = ({
  address,
  setAddress,
  pinnedAddress,
  setPinnedAddress,
  onSelectCoords,
  isMetric,
  setIsMetric,
  showCoverage,
  setShowCoverage,
  onReset,
  searchLocation,
  coverageRadius,
  setCoverageRadius,
  nearestStationsCount,
  setNearestStationsCount,
  coverageMode,
  setCoverageMode,
  sqiEnabled,
  sqiDebugAllowed,
  debugMode,
  setDebugMode,
}) => {
  const [suggestions, setSuggestions] = useState<Array<Suggestion>>([])
  const [showSuggestions, setShowSuggestions] = useState(false)
  const [highlightedIndex, setHighlightedIndex] = useState(-1)
  const [showSettings, setShowSettings] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const containerRef = useRef<HTMLFormElement>(null)
  const highlightedRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setShowSuggestions(false)
      }
    }
    document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [])

  const fetchSuggestions = useCallback(
    async (query: string): Promise<Array<Suggestion>> => {
      if (query.length < 1) {
        setSuggestions([])
        return []
      }
      // Don't show suggestions if input is valid coordinates
      try {
        new Coordinates(query)
        setSuggestions([])
        return []
      } catch {
        // Not coordinates, continue with geocoding
      }
      try {
        const res = await fetch(
          `https://api.stadiamaps.com/geocoding/v1/autocomplete?api_key=${import.meta.env.VITE_STADIA_MAPS_API_KEY}&text=${encodeURIComponent(query)}`,
        )
        if (!res.ok) {
          setSuggestions([])
          return []
        }
        const data = await res.json()
        const formatted: Array<Suggestion> = (data.features || [])
          .slice(0, 7)
          .map((f: StadiaSuggestion) => ({
            display_name: f.properties.label,
            lat: f.geometry.coordinates[1].toString(),
            lon: f.geometry.coordinates[0].toString(),
          }))
        setSuggestions(formatted)
        setHighlightedIndex(-1)
        return formatted
      } catch {
        setSuggestions([])
        return []
      }
    },
    [],
  )

  const handleAddressChange = (value: string) => {
    setAddress(value)
    setErrorMessage(null)
    setHighlightedIndex(-1)
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => fetchSuggestions(value), 300) // Debouncing every 300ms to improve performance.
    setShowSuggestions(true)
  }

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault()
    setShowSuggestions(false)
    setErrorMessage(null)
    if (!address) return

    try {
      const position = new Coordinates(address)

      if (
        position.getLatitude() < -90 ||
        position.getLatitude() > 90 ||
        position.getLongitude() < -180 ||
        position.getLongitude() > 180
      ) {
        setErrorMessage(
          "Coordinates out of bounds (latitude: -90 to 90, longitude: -180 to 180)",
        )
        return
      }

      setSuggestions([])
      setPinnedAddress(address)
      onSelectCoords(position.getLatitude(), position.getLongitude())
      return
    } catch (error) {
      // Not coordinates - continue to geocoding
    }

    if (debounceRef.current) {
      clearTimeout(debounceRef.current)
      debounceRef.current = null
    }

    const currentSuggestions =
      suggestions.length > 0 ? suggestions : await fetchSuggestions(address)

    const pickedIndex =
      highlightedIndex >= 0 && highlightedIndex < currentSuggestions.length
        ? highlightedIndex
        : 0

    if (currentSuggestions.length > 0) {
      const picked = currentSuggestions[pickedIndex]
      const newAddress = picked.display_name
      setAddress(newAddress)
      setPinnedAddress(newAddress)
      setSuggestions([])
      setHighlightedIndex(-1)
      setErrorMessage(null)
      onSelectCoords(parseFloat(picked.lat), parseFloat(picked.lon))
      return
    }

    // Try final geocoding search
    try {
      const res = await fetch(
        `https://api.stadiamaps.com/geocoding/v1/search?api_key=${import.meta.env.VITE_STADIA_MAPS_API_KEY}&text=${encodeURIComponent(address)}&limit=1`,
      )
      if (res.ok) {
        const data = await res.json()
        if (data.features && data.features.length > 0) {
          const feature = data.features[0]
          setPinnedAddress(address)
          onSelectCoords(
            feature.geometry.coordinates[1],
            feature.geometry.coordinates[0],
          )
          return
        }
      }
    } catch {
      // Geocoding failed
    }

    setErrorMessage(
      "Location not found. Make sure the address or the coordinates are valid.",
    )
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!showSuggestions || suggestions.length === 0) return
    if (e.key === "ArrowDown") {
      e.preventDefault()
      setHighlightedIndex((prev) => (prev + 1) % suggestions.length)
    } else if (e.key === "ArrowUp") {
      e.preventDefault()
      setHighlightedIndex((prev) =>
        prev <= 0 ? suggestions.length - 1 : prev - 1,
      )
    } else if (e.key === "Escape") {
      setShowSuggestions(false)
      setHighlightedIndex(-1)
    }
  }

  return (
    <>
      {/* Search bar overlay - top left */}
      <div
        style={{
          position: "absolute",
          top: 20,
          left: 20,
          zIndex: 1000,
          pointerEvents: "none",
          maxWidth: "375px",
          width: "calc(100% - 40px)",
        }}
      >
        <div style={{ pointerEvents: "auto" }}>
          <form
            ref={containerRef}
            onSubmit={handleSearch}
            className="relative group shadow-xl rounded-full"
          >
            <div
              className={`absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none transition-colors ${
                searchLocation && pinnedAddress && address === pinnedAddress
                  ? "text-swift-orange"
                  : "text-gray-400"
              }`}
            >
              <MapPin size={20} />
            </div>
            <input
              type="text"
              placeholder="Search by address or coordinates"
              className="block w-full pl-12 pr-12 py-3 bg-white border-2 border-gray-200 rounded-full focus:ring-2 focus:ring-swift-orange/20 focus:border-swift-orange outline-none transition-all text-gray-700 placeholder-gray-400 font-medium text-ellipsis"
              value={address}
              onChange={(e) => handleAddressChange(e.target.value)}
              onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
              onKeyDown={handleKeyDown}
            />
            <button
              type="submit"
              className="absolute right-2 top-1/2 -translate-y-1/2 p-2 bg-swift-orange hover:bg-swift-orange/90 text-white rounded-full flex items-center justify-center transition-all shadow-md active:scale-95"
              aria-label="Search"
              title="Search"
            >
              <Search size={20} />
            </button>

            {/* Error Message */}
            {errorMessage && (
              <div className="absolute left-0 right-0 top-full mt-2 bg-red-50 border-2 border-red-200 rounded-2xl shadow-lg z-[1100] p-4">
                <div className="flex items-start gap-3">
                  <AlertCircle
                    className="text-red-500 flex-shrink-0 mt-0.5"
                    size={18}
                  />
                  <div className="text-sm text-red-700">{errorMessage}</div>
                </div>
              </div>
            )}

            {/* Address Suggestions Dropdown */}
            {showSuggestions && suggestions.length > 0 && !errorMessage && (
              <ul className="absolute left-0 right-0 top-full mt-2 bg-white border-2 border-gray-200 rounded-2xl shadow-lg z-[1100] overflow-hidden">
                {suggestions.map((s, i) => (
                  <li key={i}>
                    <button
                      ref={i === highlightedIndex ? highlightedRef : undefined}
                      type="button"
                      onClick={() => {
                        const newAddress = s.display_name
                        setAddress(newAddress)
                        setPinnedAddress(newAddress)
                        setSuggestions([])
                        setShowSuggestions(false)
                        setHighlightedIndex(-1)
                        setErrorMessage(null)
                        onSelectCoords(parseFloat(s.lat), parseFloat(s.lon))
                      }}
                      className={`w-full text-left px-4 py-3 text-sm transition-colors flex items-center gap-3 ${
                        i === highlightedIndex
                          ? "bg-swift-orange/10 text-swift-orange"
                          : "text-gray-700 hover:bg-swift-orange/10 hover:text-swift-orange"
                      }`}
                    >
                      <MapPin size={16} className="flex-shrink-0" />
                      <span className="truncate">{s.display_name}</span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </form>
        </div>
      </div>

      {/* Settings button - bottom left, aligned with bottom of zoom controls */}
      <div className="absolute bottom-[20px] left-[20px] z-[1000] group/settings">
        <button
          onClick={() => setShowSettings(!showSettings)}
          className={`bg-white hover:bg-gray-50 text-gray-700 p-2 rounded-full shadow-xl border-2 outline-none transition-all active:scale-95 ${
            showSettings
              ? "border-swift-orange ring-2 ring-swift-orange/20"
              : "border-gray-200"
          }`}
          aria-label="Settings"
          title="Settings"
        >
          <Settings size={26} />
        </button>
      </div>

      {/* Settings panel */}
      {showSettings && (
        <div className="absolute bottom-[25px] left-[20px] z-[999] bg-white rounded-xl shadow-2xl border-2 border-gray-200 p-4 mb-12 w-60">
          <div className="space-y-4">
            {/* Coverage layer mode — first because it gates the rest. */}
            <div className="space-y-1">
              <span className="block text-xs font-bold text-gray-500 uppercase tracking-wider px-1">
                Coverage layer
              </span>
              <select
                value={coverageMode}
                onChange={(e) =>
                  setCoverageMode(e.target.value as CoverageMode)
                }
                className="w-full pl-2 pr-7 py-1.5 rounded-lg border border-gray-300 text-sm font-semibold bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-swift-orange/40"
                aria-label="Coverage layer"
              >
                <option value="baseline">Baseline Distance</option>
                {sqiEnabled && (
                  <option value="sqi">Service Quality (beta)</option>
                )}
              </select>
            </div>

            {/* Debug toggle (allowlisted users only, SQI mode only) */}
            {sqiDebugAllowed && coverageMode === "sqi" && (
              <div className="flex items-center justify-between px-1">
                <span className="text-sm font-semibold text-gray-700">
                  Debug Mode
                </span>
                <label className="cursor-pointer">
                  <input
                    type="checkbox"
                    checked={debugMode}
                    className="sr-only peer"
                    onChange={(e) => setDebugMode(e.target.checked)}
                  />
                  <div className="relative w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-swift-orange after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-swift-orange after:border-swift-orange after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-gray-300" />
                </label>
              </div>
            )}

            {/* Radius + Stations sliders only meaningful in baseline mode. */}
            {coverageMode === "baseline" && (
              <>
                {/* Units toggle */}
                <div className="space-y-2">
                  <span className="block text-xs font-bold text-gray-500 uppercase tracking-wider px-1">
                    Units
                  </span>
                  <div className="flex justify-center">
                    <Toggle
                      leftLabel="KM"
                      rightLabel="MI"
                      initialValue={isMetric}
                      onChange={(value) => setIsMetric(!value)}
                      textSize="sm"
                    />
                  </div>
                </div>

                {/* Radius slider */}
                <div className="space-y-2">
                  <div className="flex justify-between items-center px-1">
                    <span className="text-xs font-bold text-gray-500 uppercase tracking-wider">
                      Radius
                    </span>
                    <span className="text-xs font-bold text-swift-orange">
                      {isMetric
                        ? `${coverageRadius} km`
                        : `${Math.round(coverageRadius * KM_TO_MILES)} mi`}
                    </span>
                  </div>
                  <input
                    type="range"
                    min={COVERAGE_RADIUS_MIN}
                    max={COVERAGE_RADIUS_MAX}
                    step={COVERAGE_RADIUS_STEP}
                    value={coverageRadius}
                    onChange={(e) =>
                      setCoverageRadius(parseInt(e.target.value))
                    }
                    className="w-full h-1.5 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-swift-orange"
                  />
                </div>

                {/* Nearest stations count slider */}
                <div className="space-y-2">
                  <div className="flex justify-between items-center px-1">
                    <span className="text-xs font-bold text-gray-500 uppercase tracking-wider">
                      Stations
                    </span>
                    <span className="text-xs font-bold text-swift-orange">
                      {nearestStationsCount}
                    </span>
                  </div>
                  <input
                    type="range"
                    min={NEAREST_STATIONS_MIN}
                    max={NEAREST_STATIONS_MAX}
                    step={NEAREST_STATIONS_STEP}
                    value={nearestStationsCount}
                    onChange={(e) =>
                      setNearestStationsCount(parseInt(e.target.value))
                    }
                    className="w-full h-1.5 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-swift-orange"
                  />
                </div>
              </>
            )}

            {/* Coverage toggle */}
            <div>
              <button
                onClick={() => setShowCoverage(!showCoverage)}
                className={`w-full flex items-center justify-between gap-2 px-4 py-2 rounded-lg border text-sm font-semibold transition-all ${
                  showCoverage
                    ? "bg-[#46a7dd]/10 border-[#46a7dd]/40 text-[#2d7eab]"
                    : "bg-gray-50 border-gray-200 text-gray-500"
                }`}
              >
                <span>Coverage</span>
                {showCoverage ? <Eye size={16} /> : <EyeOff size={16} />}
              </button>
            </div>

            {/* Reset settings */}
            <button
              onClick={() => {
                onReset()
                setShowSettings(false)
              }}
              className="w-full px-4 py-2 rounded-lg border-2 border-gray-300 text-sm font-semibold bg-white hover:bg-gray-50 text-gray-700 transition-all"
            >
              Reset Settings
            </button>
          </div>
        </div>
      )}
    </>
  )
}

export default SearchControls
